import React, {useState, useEffect, useRef} from "react";
import {Link, useSearchParams} from "react-router-dom";
import './Zone.css'
import {motion} from 'framer-motion';
import {
    Globe,
    Building2,
    Rocket,
    NetworkIcon,
    TrendingUp,
    Gift
} from 'lucide-react';
import {Helmet} from "react-helmet-async";
import trial2 from "../../Assets/Images/trial/13-.jpg";

const zoneData = {
    "Real Estate and Construction": {
        benefits: [
            "0% Corporate and Personal Income Tax",
            "100% Foreign Ownership Allowed",
            "Strategic Location for Property Development",
            "Access to Construction Permits Fast-Track",
            "Networking with Major Real Estate Players",
            "Special Investment Visa Options"
        ],
        description: "Dubai offers exceptional benefits for real estate and construction companies, including tax advantages and streamlined permit processes.",
        icon: Building2
    },
    "Tourism and Hospitality": {
        benefits: [
            "Direct Access to Tourist Hotspots",
            "Special Tourism License Benefits",
            "Hotel Classification Support",
            "Event Planning Permissions",
            "Tourism Marketing Support",
            "Visa Facilitation for Staff"
        ],
        description: "Set up your tourism business with premium locations and exclusive hospitality sector benefits.",
        icon: Globe
    },
    "Technology and Innovation": {
        benefits: [
            "Smart City Integration Benefits",
            "R&D Support and Funding",
            "Tech Talent Visa Benefits",
            "Innovation Lab Access",
            "Government Digital Projects Access",
            "Tech Event Hosting Rights"
        ],
        description: "Access cutting-edge infrastructure and support for technology and innovation ventures.",
        icon: Rocket
    },
    "Financial Services": {
        benefits: [
            "International Banking Benefits",
            "FinTech Regulatory Sandbox Access",
            "Global Market Access",
            "Financial License Benefits",
            "Capital Market Access",
            "International Trade Support"
        ],
        description: "Enjoy world-class financial infrastructure and regulatory framework advantages.",
        icon: TrendingUp
    },
    "Retail and E-commerce": {
        benefits: [
            "Customs Duty Exemptions",
            "Warehousing Solutions",
            "E-commerce Platform Integration",
            "Logistics Support",
            "Retail Space Benefits",
            "Digital Payment Solutions"
        ],
        description: "Get comprehensive support for both physical and digital retail operations.",
        icon: NetworkIcon
    },
    "General Trading and Export": {
        benefits: [
            "Import/Export License Benefits",
            "Customs Clearance Priority",
            "Storage Facilities Access",
            "Trade Finance Support",
            "International Market Access",
            "Trading Partner Network"
        ],
        description: "Maximize your trading potential with comprehensive import/export benefits.",
        icon: Gift
    },
    "Business Consulting and Legal Services": {
        benefits: [
            "Professional Services License",
            "Legal Framework Benefits",
            "Business Network Access",
            "Professional Training Support",
            "International Client Access",
            "Regulatory Compliance Support"
        ],
        description: "Establish your professional services with premium benefits and legal framework support.",
        icon: NetworkIcon
    },
};
export default function Zone() {
    const [searchParams] = useSearchParams();
    const [selectedZone, setSelectedZone] = useState("");
    const cardRef = useRef(null);

    useEffect(() => {
        const typeFromUrl = searchParams.get("type");
        if (typeFromUrl && zoneData[typeFromUrl]) {
            setSelectedZone(typeFromUrl);
            setTimeout(() => {
                cardRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 100);
        }
    }, [searchParams]);

    const handleZoneChange = (e) => {
        const newValue = e.target.value;
        setSelectedZone(newValue);
        if (newValue) {
            setTimeout(() => {
                cardRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 100);
        }
    };

    return (
        <div className="w-full">
            <Helmet>
                <title>Business Type Selector - Dubai Business Solutions</title>
                <meta name="description"
                      content="Dubai Business Solutions offers tailored business zones designed to accelerate your growth. From tech innovation to real estate, Dubai offers unparalleled opportunities for global entrepreneurs."/>
                <link rel="canonical" href="https://www.businesssolutionsdubai.com/dubai-work-zones"/>
                <meta name="robots" content="index, follow"/>
                <meta name="keywords"
                      content="Business Zones, Dubai Business Solutions, Dubai Business, Dubai Startups, Dubai Ventures, Dubai Investment, Dubai Real Estate, Dubai Tech, Dubai Finance, Dubai Retail, Dubai E-commerce, Dubai General Trading, Dubai Banking, Dubai Business Consulting, Business Setup, Business Solutions, Business Services, Business Strategy, Business Planning, Business Development, Business Growth, Business Opportunities, Global Business, Global Entrepreneurship, Global Growth, Global Innovation, Global Investment, Global Real Estate, Global Tech, Global Finance, Global Retail, Global E-commerce, Global Trading, Global Banking, Global Business Consulting, Setup Business, Startup Business, Venture Business, Investment Business, Real Estate Business, Tech Business, Finance Business, Retail Business, E-commerce Business, Trading Business, Banking Business, Consulting Business"/>
                <meta name="lenguage" content="english"/>
                <meta name="publisher" content="Business Solutions Dubai"/>
                <link rel="preload" href="/Assets/Images/trial/13-.jpg" as="image"/>
            </Helmet>
            <div
                style={{backgroundImage: `url(${trial2})`}}
                className="bg-cover bg-center bg-no-repeat relative min-h-screen md:min-h-[70vh] flex justify-center items-center overflow-hidden w-full py-56 md:py-60"
            >
                {/* Content Container */}
                <motion.div
                    initial={{opacity: 0, y: 50}}
                    animate={{opacity: 1, y: 0}}
                    transition={{duration: 0.8}}
                    className="relative z-10 text-center max-w-4xl px-4 sm:px-6 lg:px-8"
                >
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 sm:mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-cyan-500">
                        Unlock Your Business Potential in Dubai
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl text-white mb-8 md:mb-10 max-w-3xl mx-auto leading-relaxed">
                        Discover tailored business zones designed to accelerate your growth. From tech innovation to
                        real estate,
                        Dubai offers unparalleled opportunities for global entrepreneurs.
                    </p>

                    {/* Animated Zone Highlights */}
                    <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 md:mb-12">
                        {Object.keys(zoneData).slice(0, 6).map((zone, index) => {
                            const Icon = zoneData[zone].icon;
                            return (
                                <motion.div
                                    key={zone}
                                    initial={{opacity: 0, scale: 0.8}}
                                    animate={{opacity: 1, scale: 1}}
                                    transition={{
                                        delay: index * 0.2,
                                        type: "spring",
                                        stiffness: 300
                                    }}
                                    className="bg-white p-2 sm:p-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 group w-[100px] md:w-[180px]"
                                >
                                    <Icon
                                        className="w-6 h-6 sm:w-12 sm:h-12 text-blue-500 mx-auto mb-1 sm:mb-2 group-hover:scale-110 transition-transform"
                                    />
                                    <p className="text-xs sm:text-sm font-medium text-gray-700 text-center">
                                        {zone}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Call to Action */}
                    <motion.div
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        transition={{delay: 1.2, duration: 0.5}}
                    >
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            href="https://wa.link/txunpz"
                            className="inline-flex items-center px-4 py-2 sm:px-8 sm:py-4 bg-blue-600 text-white rounded-full text-base sm:text-lg font-semibold hover:bg-blue-700 transition-colors group"
                        >
                            Explore Business Zones
                            <svg
                                className="ml-2 sm:ml-3 w-4 h-4 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                                />
                            </svg>
                        </a>
                    </motion.div>
                </motion.div>
            </div>


            <div className="flex justify-center align-center flex-col text-center h-[200px]">
                <h2 className="text-5xl my-16 font-bold">Select your business type</h2>
                <p className="text-xl">Select your business type to see the benefits we have for your companie right
                    now.</p>
            </div>


            <div className="flex flex-col items-center space-y-8 my-20">
                <select
                    className="w-full max-w-xl p-3 border rounded-lg bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={selectedZone}
                    onChange={handleZoneChange}
                >
                    <option value="">Select your business sector</option>
                    {Object.keys(zoneData).map((zone) => (
                        <option key={zone} value={zone}>
                            {zone}
                        </option>
                    ))}
                </select>

                {selectedZone && (
                    <div
                        ref={cardRef}
                        key={selectedZone}
                        className="w-full max-w-4xl animate-fade-slide-down"
                    >
                        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                            <div className="bg-gradient-to-r from-[#74ACDF] to-[#98c5eb] p-6 text-white">
                                <h2 className="text-2xl font-bold">{selectedZone}</h2>
                                <p className="mt-2 text-white/90">{zoneData[selectedZone].description}</p>
                            </div>

                            <div className="p-6">
                                <h3 className="font-semibold text-xl mb-6 text-gray-800">Key Benefits:</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {zoneData[selectedZone].benefits.map((benefit, index) => (
                                        <div
                                            key={`${selectedZone}-${index}`}
                                            className="flex items-start space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                                        >
                                            <svg
                                                className="w-5 h-5 text-green-500 mt-1 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span className="text-gray-700">{benefit}</span>
                                        </div>
                                    ))}
                                </div>

                                <button
                                    className="w-full mt-8 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center space-x-2 group">
                                    <span>Request Advisor</span>
                                    <svg
                                        className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="flex justify-center items-center w-full flex-col md:flex-row my-16 gap-16">
                <div className="w-[90%] md:w-[40%] flex justify-center items-center flex-col text-center">
                    <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
                    <Link
                        to="/dubai-work-zones"
                        className="w-ful mt-8 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center space-x-2 group"
                    >
                        <span>Find More Companies Here</span>
                        <svg
                            className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M14 5l7 7m0 0l-7 7m7-7H3"
                            />
                        </svg>
                    </Link>
                </div>
                <div className="w-full md:w-[30%] flex justify-between items-center gap-8 flex-col">
                    <div
                        className="flex justify-center align-center w-[90%] border border-2 border-gray-200 rounded-xl flex-col p-4 shadow-xl">
                        <h3 className="font-bold text-2xl my-2">What are the benefits of starting a business in
                            Dubai?</h3>
                        <p className="my-2">Dubai offers a tax-free environment, 100% foreign ownership in free zones,
                            world-class infrastructure, and access to a strategic global market.</p>
                    </div>
                    <div
                        className="flex justify-center align-center w-[90%] border border-2 border-gray-200 rounded-xl flex-col p-4 shadow-xl">
                        <h3 className="font-bold text-2xl my-2">How do I choose the right free zone for my
                            business?</h3>
                        <p className="my-2">It depends on your business activity. Free zones like DMCC, DIFC, and JAFZA
                            specialize in different industries, such as trading, finance, or logistics.</p>
                    </div>
                    <div
                        className="flex justify-center align-center w-[90%] border border-2 border-gray-200 rounded-xl flex-col p-4 shadow-xl">
                        <h3 className="font-bold text-2xl my-2">What is the process to register a business in
                            Dubai?</h3>
                        <p className="my-2">The process involves selecting a business activity, choosing a legal
                            structure, registering the trade name, and obtaining the necessary licenses and
                            approvals.</p>
                    </div>
                </div>
            </div>

        </div>
    );
}